import { createSelector } from '@reduxjs/toolkit';
import {
  ApiAttachment,
  MainFiles as MainFilesModel,
} from 'store/models/MainFiles';
import { AttPageSelected } from 'store/models/file-selected/AttPageSelected';
import { ApiAttachmentForFS } from 'store/models/MainFiles';
import {
  curBookingsBookingsSelector,
  viewingIndexSelector,
} from 'store/selectors/cur-bookings.selector';
import { ThumbnailMapI } from 'store/models/file-selected/file-selected.interface';
import { ThumbnailMap } from 'store/models/file-selected/FileSelected';
import {
  TicketApiData,
  Ticket as TicketModel,
} from 'store/models/Ticket';
import { uploadingFilesSelector } from 'store/selectors/uploading-files.selector';
import {
  ticketDataSelector,
  ticketSelector,
} from 'store/selectors/ticket.selector';

export const ticketAttachmentsSelector = createSelector(
  ticketSelector,
  (storeState): ApiAttachment[] => {
    const { attachments = [] } = storeState;
    return attachments;
  },
);

export const ticketAttachmentsRecycledAttachmentPartIdsSelector = createSelector(
  ticketAttachmentsSelector,
  (attachments: ApiAttachment[]) => {
    return TicketModel.storeAttachmentsToRecycledAttachmentPartIds(
      attachments,
    );
  },
);

/**
 * TODO: revisit the type
 */
export const ticketApiWithFilesSelector = createSelector(
  ticketDataSelector,
  ticketAttachmentsSelector,
  uploadingFilesSelector,
  (
    data: TicketApiData | undefined,
    attachments: ApiAttachment[], // already took care of isRecycled since load ticket success
    uploadingFiles = [],
  ): any => {
    if (!data) return {};
    const files = MainFilesModel.apiToMainFilesData(
      attachments,
      uploadingFiles,
    );
    data = {
      ...data,
      attachments,
    };
    return {
      ...data,
      files,
    } as any;
  },
);

export const ticketAttachmentsAvailableOnlySelector = createSelector(
  ticketAttachmentsSelector,
  (atts: ApiAttachment[]): ApiAttachment[] => {
    return atts.filter((a) => !a.isRecycled);
  },
);

export const availableOnlyThumbnailMapSelector = createSelector(
  ticketAttachmentsAvailableOnlySelector,
  (attachments: ApiAttachment[]): ThumbnailMapI => {
    if (!attachments) return {};
    return ThumbnailMap.attachmentsApiToThumbnailMap(attachments);
  },
);

/**
 * TODO: might have to add this logic in the MainFiles model class
 */
export const availableOnlyAttachmentsWithThumbnailSelector = createSelector(
  ticketAttachmentsAvailableOnlySelector,
  availableOnlyThumbnailMapSelector,
  (
    attachments: ApiAttachment[],
    thumbnailMap: ThumbnailMapI,
  ): ApiAttachment[] => {
    if (!attachments) return [];
    return attachments.map((att) => ({
      ...att,
      thumbnails: att.thumbnails.map((t) => ({
        ...t,
        pdfUrl:
          thumbnailMap[att.partId] &&
          thumbnailMap[att.partId][t.num].pdfUrl,
      })),
    }));
  },
);

export const attachmentsSelector = createSelector(
  availableOnlyAttachmentsWithThumbnailSelector,
  curBookingsBookingsSelector,
  viewingIndexSelector,
  (atts, curBookings, viewingIndex): ApiAttachmentForFS[] => {
    const attPSelectedM = new AttPageSelected(atts, curBookings);
    return atts.map((att) => ({
      ...att,
      thumbnails: att.thumbnails.map((t) => ({
        ...t,
        isInFileSelected: attPSelectedM.isAttPageInBookingFileSelected(
          att.partId,
          t.num,
          viewingIndex,
        ),
        isInOtherFileSelected: attPSelectedM.isAttPageInOtherFileSelected(
          att.partId,
          t.num,
          viewingIndex,
        ),
      })),
      canSelectPage: MainFilesModel.canSelectPage(att.ext),
    }));
  },
);
